import { useState, useCallback } from 'react';

export interface LoadingState {
  [key: string]: boolean;
}

export interface UseLoadingReturn {
  loading: LoadingState;
  isLoading: (key?: string) => boolean;
  setLoading: (key: string, isLoading: boolean) => void;
  withLoading: <T>(key: string, asyncFn: () => Promise<T>) => Promise<T>;
}

/**
 * Hook for managing loading states across multiple operations
 */
export const useLoading = (initialState: LoadingState = {}): UseLoadingReturn => {
  const [loading, setLoadingState] = useState<LoadingState>(initialState);

  const setLoading = useCallback((key: string, isLoading: boolean) => {
    setLoadingState(prev => ({
      ...prev,
      [key]: isLoading
    }));
  }, []);

  const isLoading = useCallback((key?: string) => {
    if (key) {
      return loading[key] || false;
    }
    // If no key provided, return true if any operation is loading
    return Object.values(loading).some(Boolean);
  }, [loading]);

  const withLoading = useCallback(async <T>(key: string, asyncFn: () => Promise<T>): Promise<T> => {
    setLoading(key, true);
    try {
      const result = await asyncFn();
      return result;
    } finally {
      setLoading(key, false);
    }
  }, [setLoading]);

  return {
    loading,
    isLoading,
    setLoading,
    withLoading
  };
};

/**
 * Simple loading hook for single operation
 */
export const useSimpleLoading = (initialLoading = false) => {
  const [loading, setLoading] = useState(initialLoading);

  const withLoading = useCallback(async <T>(asyncFn: () => Promise<T>): Promise<T> => {
    setLoading(true);
    try {
      const result = await asyncFn();
      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    setLoading,
    withLoading
  };
};